import { LinkOptions, SectionContainer } from '@griply/types';
import { useGoals, useTransferSection } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import React from 'react';
import { GoalTreeContextMenu } from 'shared/components/connected/goal-context-menu';
import { PopupMenu } from 'shared/components/ui/popup-menu';
import { ID } from 'shared/types/id';

export type MoveSectionContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref' | 'onOutside'
> & {
  sectionId: ID;
  // The container the section currently lives on (goal or life area).
  source: SectionContainer;
  sourceType: LinkOptions;
  // Drives `GoalTreeContextMenu`'s cycle-prevention; for a goal source this is
  // its id (forbids moving onto itself / descendants). Harmless for life areas.
  sourceId: ID;
  onClose: () => void;
};

// Reuses the goal "Move to" picker (goals + life areas) so a section can move
// into any goal or life area, then transfers it via the shared `transferSection`
// mutation. `GoalTreeContextMenu`'s `onSelect` reports the picked target as a
// parent chain (last id = target goal) or a `lifeAreaId`.
export const MoveSectionContextMenu: React.FC<MoveSectionContextMenuProps> = ({
  sectionId,
  source,
  sourceType,
  sourceId,
  onClose,
  ...rest
}) => {
  const goals = useGoals();
  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });
  const transferSection = useTransferSection();

  const onSelect = (parentIds: ID[], lifeAreaId: ID | null) => {
    if (parentIds.length) {
      const targetGoal = goals.find(
        ({ id }) => id === parentIds[parentIds.length - 1],
      );
      if (targetGoal) {
        transferSection({
          sectionId,
          from: source,
          fromType: sourceType,
          to: targetGoal,
          toType: LinkOptions.Goal,
        });
      }
      onClose();
      return;
    }

    const targetLifeArea = lifeAreaId
      ? lifeAreas.find(({ id }) => id === lifeAreaId)
      : undefined;
    if (targetLifeArea) {
      transferSection({
        sectionId,
        from: source,
        fromType: sourceType,
        to: targetLifeArea,
        toType: LinkOptions.LifeArea,
      });
    }
    onClose();
  };

  const currentSelection =
    sourceType === LinkOptions.Goal
      ? { goalId: sourceId }
      : { lifeAreaId: sourceId };

  return (
    <GoalTreeContextMenu
      id={sourceId}
      onSelect={onSelect}
      currentSelection={currentSelection}
      onClose={onClose}
      {...rest}
    />
  );
};
